import Button from '@shared/components/core/button/CoreButton';
import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import DescriptionEditor from '@shared/components/core/field/editor/DescriptionEditor';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { SubSectionContent, SubSectionDialogContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import { BodySmall } from '@shared/components/core/text/Typography';
import { getLicenseType } from '@shared/constants/organization/LicenseUtils';
import { useCurrentOrgDetailsEditor } from '@shared/hooks/useCurrentOrgDetailsEditor';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import { useTranslation } from 'react-i18next';
import { getUpgradeEntryPath } from '../billing/upgradeNavigation';
import OrgCustomBillingUrlEditor from '../details/OrgCustomBillingUrlEditor';
import OrgNameEditor from '../details/OrgNameEditor';

interface IManagedOrgDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    org: OrganizationModel;
}

function ManagedOrgDetailsDialog({ open, onClose, org }: Readonly<IManagedOrgDetailsDialogProps>) {
    const { t } = useTranslation();
    const { handleInputChange, handleUpdateWithAlerts, editOrganization, hasChanges, resetEditOrganization } =
        useCurrentOrgDetailsEditor(org ?? null);

    const handleConfirm = async () => {
        if (hasChanges) {
            const response = await handleUpdateWithAlerts();
            if (response.resultType === 'Ok') {
                handleClose();
            }
        }
    };

    const handleClose = () => {
        onClose();
        resetEditOrganization();
    };

    return (
        <ContentDialog
            fullWidth
            dialogTag={t(`common:billing.${getLicenseType(org)}.title`)}
            open={open}
            onClose={handleClose}
            title={org.name}
            description={org.description}
            onCancel={handleClose}
            onConfirm={handleConfirm}
            confirmButtonText={t('common:unsaved.confirm')}>
            <>
                <SubSectionRow title={t('common:billing.title')} variant="small">
                    <Button
                        $size="small"
                        endIcon="externalLink"
                        to={getUpgradeEntryPath({
                            organizationId: org.originatedByOrganizationId,
                            managedOrgId: org.id,
                        })}>
                        {t('common:billing.view')}
                    </Button>
                </SubSectionRow>
                <SubSectionDialogContent>
                    <BodySmall>{t('common:created', 'Created', { value: org.createdDate })}</BodySmall>

                    <BodySmall>{t('common:expires', 'Expires', { value: org.licenseExpirationDate })}</BodySmall>
                    <BodySmall>
                        {t('common:organization.member.seats.maxSeatValue', 'Max', { value: org.maxMembers ?? 0 })}
                    </BodySmall>
                    {/* <BodySmall>
                        {t('common:organization.projects.clients.max', 'Max', { value: org.maxAuthClients ?? 0 })}
                    </BodySmall>
                    <BodySmall>
                        {t('common:organization.metrics.lineChart.max', 'Max', {
                            maxConnections: org.maxConnections ?? 0,
                        })}
                    </BodySmall> */}
                </SubSectionDialogContent>
                {editOrganization && (
                    <>
                        <SubSectionRow title={t('common:organization.new.details')} variant="small">
                            <Button $size="small" to={`/org/${org.originatedByOrganizationId}/managed/${org.id}`}>
                                {t('common:organization.viewOrganization')}
                            </Button>
                        </SubSectionRow>
                        <SubSectionContent>
                            <OrgNameEditor
                                value={editOrganization.name ?? ''}
                                onUpdate={(field, value) => handleInputChange(field, value)}
                            />
                            <DescriptionEditor
                                value={editOrganization.description ?? ''}
                                onUpdate={(field, value) => handleInputChange(field, value)}
                            />
                            <OrgCustomBillingUrlEditor
                                value={editOrganization.customBillingUrl ?? ''}
                                onUpdate={(field, value) => handleInputChange(field, value)}
                            />
                        </SubSectionContent>
                    </>
                )}
            </>
        </ContentDialog>
    );
}

export default ManagedOrgDetailsDialog;
